import React from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ArrowUp, Loader } from 'lucide-react';
import { SortBy } from '../../../../types';
import { useUserTags } from '../../../../hooks/useTags';
import { TableHead, TableRow } from '../../../../components/ui/table';
import { Button } from '../../../../components/ui/button';
import { optimisticSort, fetchFiles } from '../../librarySlice';
import { RouteParams } from '../../types';
import { useQuery } from '../../../../hooks/urlHooks';

interface FileListHeaderProps {
  isLoading?: boolean;
}

const FileListHeader = ({ isLoading = false }: FileListHeaderProps) => {
  const dispatch = useDispatch();
  const { teamId, folderId } = useParams<RouteParams>();
  const { searchQuery } = useQuery();
  const { getTag, setTag } = useUserTags();
  const sortBy = (getTag('library-sort-by') as SortBy) || 'createdAt';
  const sortOrder = (getTag('library-sort-order') as 'asc' | 'desc') || 'desc';
  const [isSorting, setIsSorting] = React.useState(false);

  // Reset sorting state when loading completes
  React.useEffect(() => {
    if (!isLoading && isSorting) {
      setIsSorting(false);
    }
  }, [isLoading, isSorting]);

  const handleSort = (column: SortBy) => {
    setIsSorting(true);
    // Determine new sort order
    let newSortOrder: 'asc' | 'desc';
    if (sortBy === column) {
      // Toggle sort order if clicking same column
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // Default sort order for new column (name: asc, others: desc)
      newSortOrder = column === 'name' ? 'asc' : 'desc';
    }

    // 1. INSTANT: Optimistic sort in memory (feels instant)
    dispatch(
      optimisticSort({
        sortBy: column,
        sortOrder: newSortOrder,
      }),
    );

    // 2. Update user preferences (persisted)
    setTag({ key: 'library-sort-by', value: column });
    setTag({ key: 'library-sort-order', value: newSortOrder });

    // 3. BACKGROUND: Fetch from server to reconcile
    if (teamId && folderId) {
      dispatch(
        fetchFiles({
          teamId,
          folderId,
          page: 1,
          sortBy: column,
          sortOrder: newSortOrder,
          searchQuery,
        }),
      );
    }
  };

  // Component for sortable headers
  const SortableHeader = ({
    column,
    label,
    className = '',
  }: {
    column: SortBy;
    label: string;
    className?: string;
  }) => {
    const isActive = sortBy === column;
    const isAscending = sortOrder === 'asc';

    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleSort(column)}
        className={`h-auto px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-transparent transition-all duration-200 ${className}`}
        disabled={isSorting}
      >
        {label}
        {isActive && (
          <>
            {isSorting ? (
              <Loader className="ml-1.5 h-3 w-3 animate-spin" />
            ) : (
              <ArrowUp
                className={`ml-1.5 h-3 w-3 transition-transform duration-300 ease-out ${isAscending ? 'rotate-0' : 'rotate-180'}`}
                style={{ transformOrigin: 'center' }}
              />
            )}
          </>
        )}
      </Button>
    );
  };

  return (
    <TableRow className="hover:bg-transparent">
      {/* Name Column Header - takes remaining space */}
      <TableHead className="h-9 px-4 w-full">
        <SortableHeader column="name" label="Name" />
      </TableHead>

      {/* Date Column Header - fixed width, right-aligned */}
      <TableHead className="h-9 px-4 w-32 text-right">
        <div className="flex justify-end">
          <SortableHeader column="createdAt" label="Date" />
        </div>
      </TableHead>

      {/* Action Menu Column - Empty Header */}
      <TableHead className="h-9 w-10 pr-4" />
    </TableRow>
  );
};

export default FileListHeader;
